import React, { useState } from "react";
import { Link } from "react-router-dom"; // ✅ React Router for navigation
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw, { css } from "twin.macro";
import styled from "styled-components";
import { ReactComponent as SignUpIcon } from "feather-icons/dist/icons/user-plus.svg";
import WordSpacedText from "../components/WordSpacedText";
import Layout from "components/layouts/Layout";

// Use logo from public folder
const logo = process.env.PUBLIC_URL + "/logo512.png";

// Styled Components
const Container = tw(ContainerBase)`min-h-screen bg-primary-900 text-white font-medium flex justify-center -m-8`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12`;
const LogoLink = tw.a``;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-12 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold`;
const FormContainer = tw.div`w-full flex-1 mt-8`;

const Form = tw.form`mx-auto max-w-xs`;
const Input = tw.input`w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5 first:mt-0`;
const SubmitButton = styled.button`
  ${tw`mt-5 tracking-wide font-semibold bg-primary-500 text-gray-100 w-full py-4 rounded-lg hover:bg-primary-900 transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none`}
  .icon {
    ${tw`w-6 h-6 -ml-2`}
  }
  .text {
    ${tw`ml-3`}
  }
`;
const IllustrationContainer = tw.div`sm:rounded-r-lg flex-1 bg-purple-100 text-center hidden lg:flex justify-center items-center`;
const IllustrationLogo = tw.img`
  w-96 h-96    /* much bigger base size */
  md:w-[28rem] md:h-[28rem]   /* medium screens */
  lg:w-[32rem] lg:h-[32rem]   /* large screens */
  object-contain
`;

// Modal Styles
const ModalBackground = tw.div`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`;
const ModalContainer = tw.div`bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 lg:w-2/3 max-h-[90vh] overflow-y-auto p-8 relative`;
const ModalHeader = tw.div`font-bold text-xl md:text-2xl mb-4 text-center`;
const ModalBody = tw.div`text-sm md:text-base text-gray-700 leading-relaxed`;
const ModalClose = tw.button`absolute [top:0.5rem] [right:0.5rem] text-gray-500 hover:text-gray-800 font-bold text-2xl`;

// Highlighted Terms/Privacy Link
const HighlightedLink = styled.span`
  ${tw`cursor-pointer text-orange-600 font-bold border-b-2 border-orange-500 mx-1 hover:text-orange-800`}
`;

const SignUpPage = ({
  headingText = "Sign Up For Jamia",
  submitButtonText = "Sign Up",
  SubmitButtonIcon = SignUpIcon,
  signInUrl = "/login"
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [pin, setPin] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [modalContent, setModalContent] = useState(""); // "terms" or "privacy" or ""

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setError("You must agree to the Terms and Privacy Policy.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch("https://localhost:7101/api/Login/register", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ username, password, role, pin })
});


      if (response.ok) {
        setSuccess(true);
        setUsername("");
        setPassword("");
        setPin("");
        setAgreed(false);
      } else {
        const data = await response.json();
        setError(data.message || "Signup failed. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderModalContent = () => {
    if (modalContent === "terms") {
      return (
        <>
          <ModalHeader><WordSpacedText>Terms of Service</WordSpacedText></ModalHeader>
          <ModalBody>
            <p>Here goes your Terms of Service content. Include rules and user agreements.</p>
          </ModalBody>
        </>
      );
    } else if (modalContent === "privacy") {
      return (
        <>
          <WordSpacedText><ModalHeader>Privacy Policy</ModalHeader></WordSpacedText>
          <ModalBody>
            <WordSpacedText><p>Here goes your Privacy Policy content. Explain how user data is collected, stored, and used.</p></WordSpacedText>
          </ModalBody>
        </>
      );
    }
    return null;
  };

  return (
    
    <AnimationRevealPage>
      <Container>
        <Content>
          <MainContainer>
            <LogoLink href="#">
              <LogoImage src={logo} />
            </LogoLink>
            <MainContent>
              <WordSpacedText><Heading>{headingText}</Heading></WordSpacedText>
              <FormContainer>
                <Form onSubmit={handleSubmit}>
                  <Input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <Input
                    type="text"
                    placeholder="Pin"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    required
                  />

                  {/* Checkbox with highlighted Terms & Privacy */}
                  <WordSpacedText>
                    <div css={tw`flex items-center mt-4 text-xs text-gray-600`}>
                      <input
                        type="checkbox"
                        checked={agreed}
                        onChange={() => setAgreed(!agreed)}
                        css={tw`mr-2`}
                      />
                      I agree to Jamia's{" "}
                      <HighlightedLink onClick={() => setModalContent("terms")}>
                        Terms of Service
                      </HighlightedLink>{" "}
                      and{" "}
                      <HighlightedLink onClick={() => setModalContent("privacy")}>
                        Privacy Policy
                      </HighlightedLink>
                    </div>
                  </WordSpacedText>

                  <SubmitButton type="submit" disabled={loading || !agreed}>
                    <SubmitButtonIcon className="icon" />
                    <WordSpacedText>
                      <span className="text">
                        {loading ? "Signing Up..." : submitButtonText}
                      </span>
                    </WordSpacedText>
                  </SubmitButton>

                  {error && <p tw="mt-4 text-red-500 text-center">{error}</p>}
                  {success && <p tw="mt-4 text-green-500 text-center">Signup successful!</p>}

                  <WordSpacedText>
                    <p tw="mt-8 text-sm text-gray-600 text-center">
                      Already have an account?{" "}
                      <Link to={signInUrl} tw="border-b border-gray-500 border-dotted">
                        Sign In
                      </Link>
                    </p>
                  </WordSpacedText>
                </Form>
              </FormContainer>
            </MainContent>
          </MainContainer>
          <IllustrationContainer>
            <IllustrationLogo src={logo} alt="Jamia Logo" />
          </IllustrationContainer>

          {/* Modal */}
          {modalContent && (
            <ModalBackground onClick={() => setModalContent("")}>
              <ModalContainer onClick={e => e.stopPropagation()}>
                <ModalClose onClick={() => setModalContent("")}>×</ModalClose>
                {renderModalContent()}
              </ModalContainer>
            </ModalBackground>
          )}
        </Content>
      </Container>
    </AnimationRevealPage>
    
  );
};

export default SignUpPage;
